import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, CalendarClock, Download, Upload } from "lucide-react";
import { frApi, errMsg, inr, cardCls, btnGhost, TIER_CLS } from "./frUtils";
import PricingEditor from "./PricingEditor";

const SCHOOL = ["pre_primary", "primary", "middle", "secondary", "senior_secondary"];
const RULE = { flat: "Same fee for any subjects", per_subject: "Per subject", bundle: "Up to 2 subjects, +extra for 3rd" };
const rng = (s) => (s.min_fee === s.max_fee ? inr(s.min_fee) : `${inr(s.min_fee)} – ${inr(s.max_fee)}`);

function Row({ s, label, canEdit, onEdit, onDelete, testId }) {
    return (
        <div className="flex items-start gap-3 py-2.5" data-testid={testId}>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0F172A]">{label}</div>
                {s ? (
                    <div className="text-xs text-slate-600 mt-0.5 space-y-0.5">
                        <div className="font-bold text-[#0F172A] tabular-nums">
                            {s.mode === "monthly" ? `${inr(s.standard_fee)} / month · ${s.sessions_per_month} sessions` : `${rng(s)} per ${s.base_duration}-min session`}
                        </div>
                        {s.mode === "monthly" && <div>Negotiable {rng(s)} · package for {(s.package_weekly || []).join("/") || "—"} sessions a week</div>}
                        <div>{RULE[s.subject_rule]}{s.frequency_rates?.length > 0 && ` · ${s.frequency_rates.length} weekly-frequency rate${s.frequency_rates.length > 1 ? "s" : ""}`}{s.subject_prices?.length > 0 && ` · ${s.subject_prices.length} subject rate(s)`}</div>
                        {s.scheduled_from && <div className="text-blue-700 font-semibold inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> New version from {s.scheduled_from}</div>}
                    </div>
                ) : <div className="text-xs text-slate-400 mt-0.5">Pricing not configured</div>}
            </div>
            {canEdit && (
                <div className="flex shrink-0">
                    <button type="button" onClick={onEdit} aria-label="Edit" className="h-10 w-10 grid place-items-center text-slate-500 hover:text-[#1E3A8A]" data-testid={`${testId}-edit`}>{s ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</button>
                    {s && <button type="button" onClick={onDelete} aria-label="Delete" className="h-10 w-10 grid place-items-center text-slate-500 hover:text-rose-600" data-testid={`${testId}-delete`}><Trash2 className="w-4 h-4" /></button>}
                </div>
            )}
        </div>
    );
}

export default function PricingTab() {
    const [tier, setTier] = useState(1);
    const [data, setData] = useState(null);
    const [edit, setEdit] = useState(null);
    const fileRef = useRef(null);
    const load = useCallback(() => frApi.get("/pricing", { tier }).then(setData).catch((e) => toast.error(errMsg(e))), [tier]);
    useEffect(() => { setData(null); load(); }, [load]);

    const remove = async (s, label) => {
        if (!window.confirm(`Delete "${label}" for Tier ${tier}? It will show "Pricing not configured" until added again.`)) return;
        try { await frApi.del(`/pricing?skey=${encodeURIComponent(s.skey)}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
    };
    const exportJson = async () => {
        try {
            const d = await frApi.get("/pricing/export");
            const url = URL.createObjectURL(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }));
            Object.assign(document.createElement("a"), { href: url, download: `fee-structure-${d.exported_at.slice(0, 10)}.json` }).click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(errMsg(e)); }
    };
    const importJson = async (file) => {
        try {
            const d = JSON.parse(await file.text());
            if (!window.confirm(`Import ${d.structures?.length || 0} fee structures? Each is saved as a new version; history is kept.`)) return;
            const r = await frApi.post("/pricing/import", { structures: d.structures || [] });
            toast[r.errors.length ? "warning" : "success"](`Imported ${r.imported}${r.errors.length ? ` · ${r.errors.length} skipped (row ${r.errors[0].row}: ${r.errors[0].error})` : ""}`);
            load();
        } catch (e) { toast.error(e instanceof SyntaxError ? "Not a valid fee-structure file" : errMsg(e)); }
    };

    const tierChips = (
        <div className="flex flex-wrap gap-2" data-testid="fr-pricing-tiers">
            {[1, 2, 3, 4].map((t) => (
                <button key={t} type="button" onClick={() => setTier(t)} data-testid={`fr-pricing-tier-${t}`}
                    className={`h-10 px-4 rounded-full text-sm font-bold border ${tier === t ? "bg-[#0F172A] text-white border-[#0F172A]" : `${TIER_CLS[t]}`}`}>Tier {t}</button>
            ))}
        </div>
    );
    if (!data) return <div className="space-y-4">{tierChips}<div className="grid place-items-center p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div></div>;
    const find = (cid, board, extra = {}) => data.structures.find((s) => s.category_id === cid && (s.board || "any") === board
        && (s.commerce_subjects || null) === (extra.commerce || null) && !s.city_id && !s.grade);
    const extras = (cid) => data.structures.filter((s) => s.category_id === cid && (s.city_id || s.grade));
    const open = (category, preset, current) => setEdit({ category, preset: { tier, ...preset }, current });

    return (
        <div className="space-y-4" data-testid="fr-pricing">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                {tierChips}
                {data.can_edit && (
                    <div className="flex gap-2">
                        <button type="button" onClick={exportJson} className={btnGhost} data-testid="fr-pricing-export"><Download className="w-4 h-4" /> Export</button>
                        <button type="button" onClick={() => fileRef.current?.click()} className={btnGhost} data-testid="fr-pricing-import"><Upload className="w-4 h-4" /> Import</button>
                        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" data-testid="fr-pricing-import-file" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) importJson(f); }} />
                    </div>
                )}
            </div>
            <p className="text-sm text-slate-600" data-testid="fr-pricing-count">
                Tier {tier}: <b>{data.structures.length}</b> approved structures · 60-min sessions unless shown · ₹.{" "}
                {data.can_edit ? "Add, edit or delete any structure. Every save keeps the earlier version in history." : "View only. Management sets these prices."}
                {data.settings.extra_subject_fee != null && ` Classes 9–10: +${inr(data.settings.extra_subject_fee)}/month for the 3rd subject.`}
            </p>
            {data.categories.map((c) => {
                const school = SCHOOL.includes(c.key);
                const rows = school ? data.boards.map((b) => ({ board: b.key, label: b.name })) : [{ board: "any", label: "All learners" }];
                const commerce = c.key === "senior_secondary" ? [1, 2, 3].map((n) => ({ board: "any", commerce: n, label: `Commerce package · ${n} subject${n > 1 ? "s" : ""}${n > 1 ? " (one common teacher)" : " (individual tutor)"}` })) : [];
                return (
                    <section key={c.id} className={`${cardCls} !py-2`} data-testid={`fr-pricing-cat-${c.key}`}>
                        <div className="flex items-center justify-between pt-2">
                            <h3 className="font-heading font-bold text-[#0F172A]">{c.name}{!c.active && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">OFF</span>}</h3>
                            {data.can_edit && <button type="button" onClick={() => open(c, { board: school ? "cbse" : "any" }, null)} className="text-xs font-semibold text-[#1E3A8A] inline-flex items-center gap-1" data-testid={`fr-pricing-add-${c.key}`}><Plus className="w-3.5 h-3.5" /> City / class rule</button>}
                        </div>
                        <div className="divide-y divide-slate-100">
                            {[...rows, ...commerce].map((r) => {
                                const s = find(c.id, r.board, r);
                                const tid = `fr-pricing-${c.key}-${r.board}${r.commerce ? `-c${r.commerce}` : ""}`;
                                return <Row key={tid} s={s} label={r.label} canEdit={data.can_edit} testId={tid}
                                    onEdit={() => open(c, { board: r.board, commerce_subjects: r.commerce || null }, s)} onDelete={() => remove(s, `${c.name} · ${r.label}`)} />;
                            })}
                            {extras(c.id).map((s) => {
                                const label = [s.city_name && `City: ${s.city_name}`, s.grade && `Class: ${s.grade}`, data.boards.find((b) => b.key === s.board)?.name].filter(Boolean).join(" · ");
                                return <Row key={s.skey} s={s} label={label} canEdit={data.can_edit} testId={`fr-pricing-extra-${s.skey.replace(/\W/g, "")}`}
                                    onEdit={() => open(c, { board: s.board, city_id: s.city_id, city_name: s.city_name, grade: s.grade, commerce_subjects: s.commerce_subjects }, s)} onDelete={() => remove(s, label)} />;
                            })}
                        </div>
                    </section>
                );
            })}
            {edit && <PricingEditor {...edit} boards={data.boards} today={data.today} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
        </div>
    );
}
